// The Dashboard's Filters button — in the HEADER'S TOP-RIGHT CLUSTER, beside
// the User Guide, and only while the Dashboard tab is open (owner, 2026-09-15:
// "your filters button is inside the tab and not in the top-right corner like
// all of the others. Please move it and just use the icon").
//
// It sat in the Analytics header for a day, which put it in the one place a
// page-level tool does not live in this app: Upload, Filter, Refresh and
// Pipeline are all header-cluster glyphs on the Invoices pages, and References,
// Rules and Assignments moved their own tools there on 2026-09-14. It reaches
// the cluster through `components/ScreenActions.tsx`, the portal those three
// already use — `AnalyticsScreen.tsx` mounts it, because that is where the
// criteria state lives.
//
// AN ICON BUTTON, matching `ListFilters.tsx` and `FilterButton.tsx` exactly:
// the same `FilterRegular` glyph, brand fill while anything is narrowing the
// screen, and THE COUNT IN THE TOOLTIP rather than in a label. A number beside
// a glyph in a row of glyphs reads as a notification, not as a filter count —
// that is `ListFilters.tsx`'s own note, and the reason is the same here.
//
// IT OFFERS THE REPORTING SCREEN'S CRITERIA, not a set of its own. The owner's
// instruction was to abandon the dashboard's four narrow controls "in favor of
// the ones from the Reporting screen", so the fifteen questions, their option
// lists and their conversions all come from `lib/criteria.ts` and
// `lib/criteriaOptions.ts`. Nothing about which questions exist is decided here.
//
// A CENTERED DIALOG since D-344 (owner, 2026-10-09: "please use it to launch a
// proper Filters modal, centered on the screen"), `FilterDialog`, shared with
// the other two filter buttons; it was a popover off the corner until then.
//
// IT APPLIES ON A CLICK, NOT ON EVERY KEYSTROKE. `FilterButton.tsx` — the queue
// screens' filter — commits each change immediately, which is right when the
// rows are already in the browser. This one drives nine server aggregates plus
// a paged listing, so committing per keystroke in the two money fields would
// fire nine requests per digit. The dialog edits a DRAFT; Apply commits it
// and closes; closing any other way discards it; Reset empties the draft so
// the same Apply commits the empty set. Every filter works this way since
// D-344.
import { useEffect, useState, type ReactElement } from 'react'
import {
  Button,
  Field,
  Input,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { FilterRegular } from '@fluentui/react-icons'
import { MultiSelectField } from './MultiSelectField'
import {
  FLAG_OPTIONS,
  HELD_OPTIONS,
  STATUS_OPTIONS,
  criteriaCount,
  emptyCriteria,
  type Criteria,
} from '../lib/criteria'
import { useCriteriaOptions } from '../lib/criteriaOptions'
import { useMoneyInputStyles } from '../lib/theme'
import { FilterDialog } from './FilterDialog'
import { useDialogFormStyles } from './dialogForm'

const useStyles = makeStyles({
  // Wide enough for two controls a row, which is the arrangement the Reporting
  // form settled on — a range's start beside its end, the two Flags selectors
  // inline. `max-width` rather than a fixed width so the popover still fits a
  // narrow window.
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  // `minmax(0, 1fr)`, not `1fr`, or a long option label forces its column wider
  // and the grid stops aligning. `alignItems: end` keeps every control on one
  // baseline where a label wraps to two lines.
  control: { minWidth: 0, width: '100%' },
  // ONE HINT COLOUR. "Any" and the browser's own `mm/dd/yyyy` are both help
  // text, and help text is `colorNeutralForeground3` everywhere in this app.
  anyHint: { color: tokens.colorNeutralForeground3 },
  // The browser's half: `<input type="date">` draws `mm/dd/yyyy` from a
  // pseudo-element and there is no placeholder to colour, so this paints the
  // whole datetime-edit region — and only while the field is empty.
  datePlaceholder: {
    '& input::-webkit-datetime-edit': { color: tokens.colorNeutralForeground3 },
  },
})

/**
 * What an EMPTY date field shows before the browser's own `mm/dd/yyyy`.
 *
 * A native `<input type="date">` ignores `placeholder` — the `mm/dd/yyyy` is the
 * control's own rendering and cannot be replaced. `contentBefore` puts "Any" in
 * front of it inside the field, the same slot the money inputs put their `$` in.
 * ONLY WHILE EMPTY: "Any 08/01/2026" would read as a value, not as a hint.
 */
function anyPrefix(value: string, className: string): ReactElement | null {
  return value === '' ? <span className={className}>Any</span> : null
}

interface Props {
  value: Criteria
  onChange: (next: Criteria) => void
  /** The companies the Company picker offers: the Dashboard seats' (D-258). */
  companyIds?: readonly string[]
}

export function DashboardFilterButton({ value, onChange, companyIds }: Props) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const ms = useMoneyInputStyles()
  const options = useCriteriaOptions(companyIds)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Criteria>(value)

  // Re-seed the draft from the committed value each time the popover opens, so
  // closing without Apply genuinely discards and a second open never shows last time's
  // abandoned edits.
  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const set = <K extends keyof Criteria>(key: K, next: Criteria[K]): void =>
    setDraft((prev) => ({ ...prev, [key]: next }))

  const active = criteriaCount(value)
  // The accessible name AND the tooltip, so the count cannot fall out of step
  // with the label the way a separate `aria-label` would.
  const label = active === 0 ? 'Filters' : `Filters (${active})`

  return (
    <>
      <Tooltip content={label} relationship="label">
        <Button icon={<FilterRegular />} appearance={active > 0 ? 'primary' : 'secondary'} onClick={() => setOpen(true)} />
      </Tooltip>
      <FilterDialog
        open={open}
        onClose={() => setOpen(false)}
        wide
        onReset={() => setDraft(emptyCriteria)}
        onApply={() => onChange(draft)}
      >
        {(host) => (
          <>
            <div className={s.section}>
              <span className={s.sectionTitle}>Invoice-Level Filters</span>
              <div className={f.grid2}>
                <MultiSelectField
                  popupHost={host}
                  label="Companies"
                  options={options.companies}
                  selected={draft.companies}
                  onChange={(v) => set('companies', v)}
                />
                <MultiSelectField
                  popupHost={host}
                  label="Vendors"
                  search={options.searchVendorOptions}
                  selected={draft.vendors}
                  onChange={(v) => set('vendors', v)}
                />
                <Field label="Received Date From">
                  <Input
                    className={mergeClasses(s.control, draft.receivedStart === '' && s.datePlaceholder)}
                    type="date"
                    contentBefore={anyPrefix(draft.receivedStart, s.anyHint)}
                    value={draft.receivedStart}
                    onChange={(_, d) => set('receivedStart', d.value)}
                  />
                </Field>
                <Field label="Received Date To">
                  <Input
                    className={mergeClasses(s.control, draft.receivedEnd === '' && s.datePlaceholder)}
                    type="date"
                    contentBefore={anyPrefix(draft.receivedEnd, s.anyHint)}
                    value={draft.receivedEnd}
                    onChange={(_, d) => set('receivedEnd', d.value)}
                  />
                </Field>
                <Field label="Invoice Date From">
                  <Input
                    className={mergeClasses(s.control, draft.invoiceStart === '' && s.datePlaceholder)}
                    type="date"
                    contentBefore={anyPrefix(draft.invoiceStart, s.anyHint)}
                    value={draft.invoiceStart}
                    onChange={(_, d) => set('invoiceStart', d.value)}
                  />
                </Field>
                <Field label="Invoice Date To">
                  <Input
                    className={mergeClasses(s.control, draft.invoiceEnd === '' && s.datePlaceholder)}
                    type="date"
                    contentBefore={anyPrefix(draft.invoiceEnd, s.anyHint)}
                    value={draft.invoiceEnd}
                    onChange={(_, d) => set('invoiceEnd', d.value)}
                  />
                </Field>
                <Field label="Total From">
                  <Input
                    className={s.control}
                    type="number"
                    contentBefore="$"
                    input={{ className: ms.moneyText }}
                    value={draft.totalStart}
                    placeholder="Any"
                    onChange={(_, d) => set('totalStart', d.value)}
                  />
                </Field>
                <Field label="Total To">
                  <Input
                    className={s.control}
                    type="number"
                    contentBefore="$"
                    input={{ className: ms.moneyText }}
                    value={draft.totalEnd}
                    placeholder="Any"
                    onChange={(_, d) => set('totalEnd', d.value)}
                  />
                </Field>
              </div>
            </div>

            <div className={s.section}>
              <span className={s.sectionTitle}>Line Item-Level Filters</span>
              <div className={f.grid2}>
                <MultiSelectField
                  popupHost={host}
                  label="Accounts"
                  options={options.accounts}
                  selected={draft.accounts}
                  onChange={(v) => set('accounts', v)}
                />
                <MultiSelectField
                  popupHost={host}
                  label="Departments"
                  options={options.departments}
                  selected={draft.departments}
                  onChange={(v) => set('departments', v)}
                />
                <MultiSelectField
                  popupHost={host}
                  label="Codes"
                  options={options.codes}
                  selected={draft.codes}
                  onChange={(v) => set('codes', v)}
                />
              </div>
            </div>

            <div className={s.section}>
              <span className={s.sectionTitle}>Current State</span>
              <div className={f.grid2}>
                <MultiSelectField
                  popupHost={host}
                  label="Status"
                  options={STATUS_OPTIONS}
                  selected={draft.statuses}
                  onChange={(v) => set('statuses', v)}
                />
                {/* Held and Not Held, either or both; both is Any (owner, 2026-10-14). */}
                <MultiSelectField
                  popupHost={host}
                  label="On Hold"
                  options={HELD_OPTIONS}
                  selected={draft.held}
                  onChange={(v) => set('held', v)}
                />
                <MultiSelectField
                  popupHost={host}
                  label="Total Flags"
                  options={FLAG_OPTIONS}
                  selected={draft.totalFlags}
                  onChange={(v) => set('totalFlags', v)}
                />
                <MultiSelectField
                  popupHost={host}
                  label="Unresolved Flags"
                  options={FLAG_OPTIONS}
                  selected={draft.unresolvedFlags}
                  onChange={(v) => set('unresolvedFlags', v)}
                />
              </div>
            </div>

          </>
        )}
      </FilterDialog>
    </>
  )
}
